import React from "react";
import { useLocation } from "react-router-dom";
import "./estresResultPage.css";
import TrafficLightIndicatorBurnoutExhaustion from "../../../components/results/traficLight/trafficLightIndicatorBurnoutExhaustion";
import TrafficLightIndicatorBurnoutCynicism from "../../../components/results/traficLight/trafficLightIndicatorBurnoutCynicism";
import TrafficLightIndicatorBurnoutEfficacy from "../../../components/results/traficLight/trafficLightIndicatorBurnoutEfficacy";
import TrafficLightIndicatorEstresPhysical from "../../../components/results/traficLight/trafficLightIndicatorEstresPhysical";
import TrafficLightIndicatorBurnout from "../../../components/results/traficLight/trafficLightIndicatorBurnout";
import estressRecomendations from "../../../data/estressRecomendations";

const EstresResultPage = () => {
  const location = useLocation();
  const results = location.state?.response;

  if (!results) {
    return <h2>No hay datos disponibles.</h2>;
  }

  return (
    <div className="estres-result-page">

      {/* Botón para imprimir resultados */}
      <button
        className="print-button"
        onClick={() => {
          // Marca que imprimimos la sección de estrés
          document.body.classList.add("printing-estres");
          window.print();
          // Después de imprimir, quitamos la marca
          window.onafterprint = () => {
            document.body.classList.remove("printing-estres");
          };
        }}
      >
        Imprimir resultados
      </button>

      {/* Encabezado principal */}
      <header className="result-header">
        <h1>Cuestionario de Estrés y Burnout</h1>
        <p>Conoce el resumen de tus resultados y detecta posibles áreas de riesgo.</p>
      </header>

      {/* Información contextual */}
      <section className="info-card card">
        <p>
          El estrés agudo se manifiesta con un conjunto de síntomas físicos y emocionales. 
          En el cuestionario aplicado, evaluamos la frecuencia e intensidad de estos síntomas 
          para determinar el nivel de estrés vivido en el momento.
        </p>
        <div className="divider" />
        <p>
          De tener un mal manejo, este estrés agudo puede evolucionar a síndrome de burnout, 
          depresión o ansiedad.
        </p>
      </section>

      {/* Dimensiones de Maslach */}
      <section className="maslach-card card">
        <h2>En la Escala de Maslach, el agotamiento se mide en tres dimensiones:</h2>
        <p>
          <strong>Cansancio Emocional:</strong> sensación de falta de energía y recursos 
          emocionales.
        </p>
        <p>
          <strong>Despersonalización o Cinismo:</strong> actitud negativa y deshumanizada.
        </p>
        <p>
          <strong>Eficacia Laboral:</strong> sensación de logro y satisfacción; 
          a mayor eficacia, menor riesgo de burnout.
        </p>
      </section>

      {/* Resumen General */}
      <section className="result-summary card">
        <h2>Resumen General</h2>
        <div className="summary-content">
          <div className="indicator-item">
            <TrafficLightIndicatorEstresPhysical
              category="Nivel de estrés físico:"
              riskLevel={results.physicalStress}
              recommendation={estressRecomendations[results.physicalStress]}
            />
          </div>
          <div className="burnout-indicator">
            <TrafficLightIndicatorBurnout
              category="Indicador de Burnout:"
              riskLevel={results.hasBurnOut ? 1 : 0}
            />
          </div>
        </div>
      </section>

      {/* Detalle de Dimensiones de Burnout */}
      <section className="result-details">
        <h2>Detalle de Dimensiones de Burnout</h2>
        <div className="grid-dimensions">
          <div className="dimension-card">
            <TrafficLightIndicatorBurnoutExhaustion
              category="Agotamiento emocional:"
              riskLevel={results.exhaustionLevel}
            />
          </div>
          <div className="dimension-card">
            <TrafficLightIndicatorBurnoutCynicism
              category="Desconexión laboral:"
              riskLevel={results.cynicismLevel}
            />
          </div>
          <div className="dimension-card">
            <TrafficLightIndicatorBurnoutEfficacy
              category="Eficacia profesional:"
              riskLevel={results.efficacyLevel}
            />
          </div>
        </div>
      </section>

      {/* Comparativa Estrés vs. Burnout */}
      <section className="comparison-section">
        <h2>Comparación: Estrés Laboral vs. Burnout</h2>
        <div className="comparative-cards">
          <div className="card compare-card">
            <h3>Estrés Laboral</h3>
            <ul>
              <li>Muy involucrado</li>
              <li>Emociones sobrerreactivas</li>
              <li>Produce urgencia e hiperactividad</li>
              <li>Irritabilidad</li>
              <li>Daño físico predominante</li>
              <li>Conduce a trastornos de ansiedad</li>
              <li>Baja productividad por descuidos</li>
            </ul>
          </div>
          <div className="card compare-card">
            <h3>Burnout</h3>
            <ul>
              <li>Falta de involucramiento</li>
              <li>Emociones embotadas</li>
              <li>Indefensión y desesperanza</li>
              <li>Agotamiento mental</li>
              <li>Daño emocional predominante</li>
              <li>Desapego y depresión</li>
              <li>Falta de motivación en la vida</li>
            </ul>
          </div>
        </div>
      </section>


      {/* Recomendaciones Generales */}
      <section className="recomendations-section card">
        <h2>Recomendaciones Generales</h2>
        <ol>
          <li>
            <strong>Pausas Laborales:</strong> tómate 5 min de relajación aproximadamente cada hora. 
            Te recomendamos realizar técnicas de relajación (respiraciones conscientes, meditaciones 
            guiadas, escuchar música) mezcladas con estiramientos.
          </li>
          <li>
            <strong>Organiza tu agenda y tus pendientes:</strong> explora diferentes técnicas para 
            priorizar tus tareas, pues pueden percibirse en el cuerpo como factores estresores. 
            Estudia métodos como Pomodoro, GTD (Get Things Done) o la matriz de Eisenhower.
          </li>
          <li>
            <strong>Habilidades blandas:</strong> practica la comunicación asertiva y respetuosa 
            sobre tus necesidades y preocupaciones. Mejora la escucha activa de tus compañeros, 
            aprende a pedir ayuda y a poner límites cuando excedan tus capacidades.
          </li>
          <li>
            <strong>Estilo de vida saludable:</strong> evita estresores como tabaco, exceso de 
            cafeína o alcohol. Mantén una rutina de ejercicio (libera endorfinas y controla el estrés), 
            horarios regulares de alimentación y una adecuada rutina de sueño.
          </li>
          <li>
            <strong>Busca ayuda profesional:</strong> si tus síntomas de estrés y burnout persisten 
            más de dos meses, o afectan tu desempeño laboral y tu vida personal, considera terapia 
            psicológica. Un profesional puede orientarte e, incluso, derivarte a atención psiquiátrica.
          </li>
        </ol>
      </section>
    </div>
  );
};

export default EstresResultPage;
